import { useState } from "react";
import { motion } from "framer-motion";
import { SectionShell, EmptyState } from "./common";
import { FormDialog } from "./FormDialog";
import { AddToCalendar } from "./AddToCalendar";
import { EventArt } from "./EventArt";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCollection } from "@/hooks/useCollection";
import { CalendarPlus, CalendarHeart, MapPin, Clock, User, ExternalLink } from "lucide-react";
import { analytics } from "@/lib/analytics";
import { REGIONS, REGION_ORDER } from "@/lib/ncRegions";

// Keep in sync with backend/ingest/sources.py CATEGORIES
const CATEGORIES = ["All","Festival","Religious","Concert","Cultural","Kids & Family","Sports","Food","Tech Networking","Community"];
const FIELDS = [
  { name:"title", label:"Event title", type:"text", required:true, placeholder:"e.g. Grand Diwali Night 2026" },
  { name:"category", label:"Category", type:"select", required:true, options:CATEGORIES.slice(1) },
  { name:"city", label:"City", type:"city", required:true, placeholder:"Start typing — Cary, Charlotte, anywhere in NC" },
  { name:"venue", label:"Venue", type:"text", placeholder:"e.g. Koka Booth Amphitheatre" },
  { name:"date", label:"Date", type:"date", required:true },
  { name:"description", label:"Description", type:"textarea", required:true, placeholder:"Tell the community what to expect..." },
  { name:"organizer", label:"Organizer", type:"text", default:"Community Member" },
  { name:"contact", label:"Contact (email or phone)", type:"text", placeholder:"How can people reach you?" },
  { name:"photos", label:"Event photos (first photo = cover image)", type:"images", multiple:true },
];
function fmtDate(d) { const dt=new Date(d); return isNaN(dt)?d:dt.toLocaleDateString(undefined,{weekday:"short",month:"short",day:"numeric",year:"numeric"}); }
export function EventsSection() {
  const [category, setCategory] = useState("All");
  const [region, setRegion] = useState("all");
  const [city, setCity] = useState("");
  const params = {};
  if (category !== "All") params.category = category;
  if (region !== "all") params.region = region;
  if (city) params.city = city;
  const { items, loading, prepend } = useCollection("events", params);
  return (
    <SectionShell id="events" alt overline="Community Events" title="What's happening this weekend"
      subtitle="Diwali nights. Holi festivals. Cricket tournaments. Biryani cook-offs. Tech networking. By the community, for the community."
      action={<FormDialog resource="events" title="Submit an event" description="Be among the very first listed. We expect 50+ events in the first month." fields={FIELDS} onCreated={prepend} submitLabel="Submit Event" testIdPrefix="event"
        trigger={<Button className="rounded-full px-6"><CalendarPlus className="w-4 h-4 mr-2" />Submit your event</Button>} />}>
      {/* Region — where in NC */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-[12px] text-muted-foreground mr-1">Near</span>
        {[["all","All NC"], ...REGION_ORDER.map(k => [k, REGIONS[k].label])].map(([key, label]) => (
          <button key={key} onClick={() => { setRegion(key); analytics.filterRegion(`events:${key}`); }}
            className={`px-3 py-1 rounded-full text-[12px] font-medium transition-colors border ${
              region === key ? "bg-foreground text-background border-foreground" : "bg-transparent text-muted-foreground border-border hover:text-foreground"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {/* Category + city */}
      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        <div className="flex flex-wrap gap-2">{CATEGORIES.map(c=><button key={c} onClick={()=>setCategory(c)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${category===c?"bg-primary text-primary-foreground border-primary":"bg-card text-muted-foreground border-border hover:text-foreground"}`}>{c}</button>)}</div>
        <input type="text" value={city} onChange={e=>setCity(e.target.value)} placeholder="Or type a city…" className="h-9 rounded-full px-4 text-sm border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
      </div>
      {!loading && items.length===0 ? <EmptyState icon={CalendarHeart} message="No events listed yet. Know something happening nearby? Share it — you might make someone's weekend." /> :
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{items.map((e,i)=>(
          <motion.article key={e.id} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.5,delay:Math.min(i*0.05,0.3)}} className="group rounded-2xl overflow-hidden border border-border bg-card shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
            <div className="relative h-44 overflow-hidden">
              <EventArt photo={e.photo} title={e.title || e.name} category={e.category || e.cat} alt={e.category || e.cat || ""} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <Badge className="absolute top-3 left-3 rounded-full bg-primary text-primary-foreground border-0">{e.category||e.cat}</Badge>
            </div>
            <div className="p-6">
              <h3 className="font-serif text-xl font-semibold text-foreground mb-3 leading-snug">{e.title || e.name}</h3>
              <div className="space-y-1.5 text-sm text-muted-foreground mb-3">
                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-primary" />{fmtDate(e.date)}{e.time ? ` · ${e.time}` : ""}</div>
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" />{e.venue?`${e.venue}, ${e.city}`:e.city}</div>
                {e.organizer&&<div className="flex items-center gap-2"><User className="w-4 h-4 text-primary" />{e.organizer}</div>}
              </div>
              <p className="text-sm text-foreground/80 leading-relaxed line-clamp-3">{e.description||e.desc}</p>
              <AddToCalendar event={e} />
              {e.source && e.source !== "manual" && e.source_url && (
                <a
                  href={e.link || e.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => analytics.clickOutbound(`event-source-${e.source}`)}
                  className="mt-3 pt-3 border-t border-border flex items-center gap-1 text-[12px] text-muted-foreground hover:text-primary transition-colors"
                >
                  via {e.source_name || e.source} <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </motion.article>
        ))}</div>}
    </SectionShell>
  );
}
